import type { CategoryContent } from './row.js';

export const INORGANIC_CHEMISTRY: CategoryContent = {
  bscba: [
    [
      'What is the coordination number of the metal center in the complex [Cu(NH3)4]2+?',
      [
        '4',
        '2',
        '3',
        '6',
      ],
      0,
      'Four ammonia ligands directly bond to Cu(II), giving a coordination number of 4.',
    ],
    [
      'In crystal field theory, how does the energy of d-orbitals split in an octahedral metal complex?',
      [
        'The d-orbitals split into two energy sets',
        'All five d-orbitals gain energy equally',
        'The d-orbitals split into s- and p-orbitals',
        'Only the dz2 orbital is affected by the ligand field',
      ],
      0,
      'Octahedral ligands break d-orbital degeneracy; t2g orbitals lower and eg orbitals raise relative to the barycenter.',
    ],
    [
      'What characterizes a Lewis acid-base interaction in coordination chemistry?',
      [
        'An electron pair donor bonds to an acceptor',
        'Transfer of protons between species',
        'Formation of ionic bonds between neutral ligands and metal ions',
        'Breaking of sigma bonds in the ligand',
      ],
      0,
      'Lewis acid (metal) accepts electrons from Lewis base (ligand), forming a coordinate covalent bond.',
    ],
    [
      'What is the primary difference between octahedral and tetrahedral coordination geometries?',
      [
        'Octahedral has 6 ligands; tetrahedral has 4',
        'Both geometries always have the same coordination number',
        'Tetrahedral is always more stable than octahedral',
        'The difference is only in color, not geometry',
      ],
      0,
      'Octahedral (CN=6, d-orbital splitting ~1.2 eV) is typical for d6–d9 metals; tetrahedral (CN=4, larger splitting) favors d0, d5, d10.',
    ],
    [
      'In transition metal chemistry, what does the spectrochemical series arrange?',
      [
        'Ligands ranked by crystal field splitting',
        'Metals in order of oxidation state stability',
        'Wavelengths of light absorbed by complexes',
        'Thermal stability of coordination compounds',
      ],
      0,
      'The series ranks ligands by Δ-splitting strength: CN− and CO are strong-field; I−, Br− are weak-field.',
    ],
    [
      'What type of isomerism is exhibited by [Co(NH3)5Cl]2+ and [Co(NH3)4(OH)]Cl?',
      [
        'Ionization isomer',
        'Linkage isomerism',
        'Geometric isomerism',
        'Optical isomerism',
      ],
      0,
      'Ionization isomers differ in which anions are coordinated vs. counterions; one has Cl− bound, the other as a free ion.',
    ],
    [
      'In solid-state chemistry, what is a lattice parameter?',
      [
        'The distance between equivalent lattice points',
        'The number of atoms per unit cell',
        'The angle at which atoms repel each other',
        'The vibration frequency of atoms at equilibrium',
      ],
      0,
      'Lattice parameters (a, b, c, α, β, γ) define unit cell dimensions and are directly measurable by X-ray diffraction.',
    ],
    [
      'Which type of bond is characteristic of organometallic chemistry?',
      [
        'A direct metal-to-carbon covalent bond',
        'An ionic interaction via hydride transfer',
        'A hydrogen bond mediated by a bridging atom',
        'A van der Waals contact with no covalent character',
      ],
      0,
      'Organometallic compounds contain M–C σ bonds (alkyl) and π bonds (alkene, alkyne, carbene coordination).',
    ],
    [
      'What is the primary role of a catalyst in a metal-complex-catalyzed reaction?',
      [
        'Lower activation energy without being consumed',
        'Shift the equilibrium constant to favor products',
        'Increase the enthalpy of the reaction',
        'Supply heat energy to the reaction system',
      ],
      0,
      'Catalysts lower Ea and are regenerated at the cycle end; they increase reaction rate but not ΔG.',
    ],
    [
      'In acid-base chemistry, what distinguishes a Brønsted acid from a Lewis acid?',
      [
        'Brønsted acids donate protons; Lewis acids accept electron pairs',
        'Brønsted acids are always stronger than Lewis acids',
        'Brønsted bases are related to Lewis bases by ionic bonding',
        'Lewis acids must be positively charged; Brønsted acids may be neutral',
      ],
      0,
      'Brønsted: H+ donor/acceptor. Lewis: electron pair acceptor/donor. All Brønsted acids are Lewis acids, but not vice versa (e.g., BF3).',
    ],
    [
      'What is hemoglobin\'s primary ligand in reversible oxygen binding?',
      [
        'The iron(II) center in a heme group',
        'Histidine residues directly bind molecular oxygen',
        'Sulfide bridges coordinate oxygen molecules',
        'Carboxylate groups form reversible O2 adducts',
      ],
      0,
      'Fe(II) in heme porphyrin IX binds O2 reversibly; myoglobin operates similarly with higher oxygen affinity.',
    ],
    [
      'In the complex [Fe(CN)6]4−, how many electron pairs does cyanide donate to iron?',
      [
        '6 electron pairs',
        '3 electron pairs',
        '6 unpaired electrons',
        '12 lone pairs from the ligands',
      ],
      0,
      'CN− donates 1 σ lone pair (2 electrons); six ligands donate 12 electrons total to form a very low-spin Fe(II) complex.',
    ],
    [
      'What is the typical coordination number for a tetrahedral complex?',
      [
        '4',
        '6',
        '8',
        '2',
      ],
      0,
      'Tetrahedral coordination places four ligands at the vertices of a tetrahedron around the central metal (CN = 4).',
    ],
    [
      'Which factor primarily determines whether a transition metal complex is colored?',
      [
        'd-orbital splitting allows visible-light transitions',
        'The presence of paramagnetic electrons in the complex',
        'The charge of the complex ion',
        'The mass of the ligand atoms',
      ],
      0,
      'Color arises from d–d transitions (if Δ falls in visible range, 400–700 nm) or charge-transfer absorptions.',
    ],
    [
      'What is the chelate effect, and why do chelating ligands form more stable complexes?',
      [
        'Multidentate ligands bind via multiple donor sites',
        'Chelating ligands form weaker bonds than monodentate equivalents',
        'Chelation requires ionic bonding only',
        'Chelating agents always contain nitrogen',
      ],
      0,
      'Chelation: multiple donors reduce the number of particles in solution (favorable ΔS); each donor-metal bond contributes to binding (cumulative ΔH).',
    ],
  ],
  msc: [
    [
      'Why does CN− cause larger crystal field splitting (Δ) than F− despite F− being smaller?',
      [
        'CN− has π-backbonding while F− is purely σ-donating',
        'F− is a stronger field ligand because it is more electronegative',
        'Both ligands cause identical d-orbital splitting',
        'CN− and F− are incapable of forming octahedral complexes',
      ],
      0,
      'Strong-field ligands (CN−, CO) exploit π-backbonding; weak-field ligands (F−) donate σ only. π-interactions dramatically increase Δ.',
    ],
    [
      'In ligand field theory, what is the role of π-backbonding in metal-carbonyl complexes?',
      [
        'Metal d-electrons donate into CO\'s π* orbitals',
        'Ligand electrons are transferred irreversibly to the metal d-orbitals',
        'The metal oxidation state increases due to electron loss',
        'π-bonding is destructive and weakens complex stability',
      ],
      0,
      'Synergic effect: M→CO σ + CO←M π reinforce each other; π-backbonding lengthens C≡O and weakens it, increasing metal–ligand bond strength.',
    ],
    [
      'How does the Jahn–Teller effect manifest in octahedral Cu(II) d9 complexes?',
      [
        'Axial elongation or compression removes degeneracy',
        'The complex spontaneously expels one ligand to become square-pyramidal',
        'The metal oxidizes to Cu(III) to achieve a d8 configuration',
        'All octahedral Cu(II) complexes are perfectly symmetric',
      ],
      0,
      'Cu(II) d9 has one unpaired eg electron; distortion lifts this electron\'s degeneracy and stabilizes the complex (Jahn–Teller theorem).',
    ],
    [
      'What is the relationship between magnetic moment and unpaired d-electrons in transition metal complexes?',
      [
        'μ = √[n(n+2)] μB, where n is unpaired electrons',
        'Magnetic moment is determined solely by atomic number',
        'Paired electrons in d-orbitals completely cancel all magnetism',
        'Only s-electrons contribute to the overall magnetic moment',
      ],
      0,
      'Spin-only approximation: μ ∝ √[n(n+2)]; orbital contributions and spin–orbit coupling refine this for first-row transition metals.',
    ],
    [
      'In the NaCl (rock-salt) structure, what is the coordination sphere of each ion?',
      [
        'Each Na+ is octahedrally surrounded by 6 Cl−, and each Cl− by 6 Na+',
        'Each ion is tetrahedrally coordinated to 4 opposite-charge neighbors',
        'Na+ and Cl− form alternating chains with CN = 2',
        'The structure is amorphous with no long-range order',
      ],
      0,
      'Rock-salt: both Na+ and Cl− adopt octahedral coordination (CN = 6) in an alternating FCC lattice. This high CN increases electrostatic stability.',
    ],
    [
      'What is the chelate effect, and how does it differ from simple ligand displacement?',
      [
        'Chelation offers extra entropic and enthalpic stabilization',
        'Monodentate ligands form stronger bonds than chelating ligands',
        'Chelation requires ionic bonding exclusively',
        'Chelating agents must contain only nitrogen donors',
      ],
      0,
      'Chelate effect: bidentate ligands increase ΔS (fewer particles) and provide cumulative ΔH; EDTA binds metal ions far more tightly than NH3.',
    ],
    [
      'How does electron density on the metal affect the reactivity of an organometallic complex?',
      [
        'Electron-rich metals are nucleophilic, electron-poor electrophilic',
        'All metals exhibit identical reactivity regardless of electronic environment',
        'Only d10 metals are reactive in organometallic catalysis',
        'The ligand environment has no effect on metal reactivity',
      ],
      0,
      'Metal electronics (oxidation state, d-count, L-environment) determine electron density and reactivity: high e− density → nucleophilic.',
    ],
    [
      'What is the difference between "high-spin" and "low-spin" configurations in octahedral complexes?',
      [
        'Weak field gives high-spin; strong field gives low-spin',
        'High-spin and low-spin refer to the nuclear spin state',
        'High-spin complexes are always tetrahedral; low-spin are octahedral',
        'The terms describe ligand steric bulk, not electronic effects',
      ],
      0,
      'Spin state depends on pairing energy P vs. splitting Δ: weak-field (Δ < P) → high-spin; strong-field (Δ > P) → low-spin.',
    ],
    [
      'In redox catalysis, how does the electron-transfer chain regenerate the catalyst?',
      [
        'Electron transfer regenerates the catalyst each cycle',
        'The metal center is permanently oxidized or reduced',
        'All electrons transfer simultaneously in a single step',
        'Reversibility is prevented to drive the reaction forward',
      ],
      0,
      'Catalytic cycle: M^n+ + substrate → M^(n±1) + product intermediate → M^n+ regenerated; the catalyst returns to its original oxidation state.',
    ],
    [
      'What structural role do histidine residues play in hemoproteins?',
      [
        'Histidine is the fifth axial heme ligand',
        'Histidine directly binds oxygen molecules',
        'Histidine is bonded to the peptide backbone only',
        'Iron(III) hemoproteins never bind oxygen',
      ],
      0,
      'In hemoglobin and myoglobin, proximal histidine (imidazole N) bonds Fe(II) axially; the distal histidine assists O2 stabilization.',
    ],
    [
      'How does metal-to-ligand charge transfer (MLCT) differ from ligand-to-metal charge transfer (LMCT)?',
      [
        'MLCT: metal to ligand π*; LMCT: ligand to metal d-orbital',
        'Both processes are identical and the terms are interchangeable',
        'MLCT only occurs in neutral complexes; LMCT only in cationic species',
        'LMCT absorptions always occur at higher energy than MLCT',
      ],
      0,
      'MLCT favors low-valent, electron-rich metals (Ru(II)); LMCT favors high-valent, electron-poor metals (Fe(III)) and anionic ligands (O2−, S2−).',
    ],
    [
      'What is the empirical relationship between crystal field splitting (Δ) and absorption wavelength in d–d transitions?',
      [
        'Photon energy equals Δ; larger Δ gives shorter wavelengths',
        'Δ is independent of absorption wavelength',
        'Longer wavelengths always correspond to larger crystal field splittings',
        'Absorption wavelength is determined by ligand mass, not splitting energy',
      ],
      0,
      'Eₚₕₒₜₒₙ = hc/λ = Δ; strong-field complexes (large Δ) absorb at λ ~400 nm (blue); weak-field (small Δ) at λ ~900 nm (infrared).',
    ],
    [
      'In cytochrome P450 oxidation catalysis, what is the role of the iron center?',
      [
        'Iron cycles oxidation states to form a high-valent oxo species',
        'Iron is a structural scaffold with no catalytic role',
        'Iron exclusively binds nitrogen and cannot interact with oxygen',
        'The protein backbone performs all oxygen activation',
      ],
      0,
      'P450: Fe(II) binds O2 → forms Fe(IV)=O (high-valent oxo) → hydroxylates C–H; essential for drug metabolism and biosynthesis.',
    ],
    [
      'What is the "18-electron rule" in organometallic chemistry?',
      [
        'Stable complexes often have 18 valence electrons total',
        'All metals must have exactly 18 d-electrons regardless of ligand',
        'The rule applies exclusively to main-group elements',
        '18 electrons guarantee complex decomposition',
      ],
      0,
      '18-electron rule: d10 + filled s/p = closed-shell analog to noble gas (Ar/Kr); promotes kinetic and thermodynamic stability.',
    ],
    [
      'How does the nephelauxetic effect relate to complex color in transition metals?',
      [
        'Ligand electrons expand d-orbitals, lowering Δ',
        'Ligands increase effective nuclear charge on metal electrons',
        'Nephelauxetic effects always blue-shift complex colors',
        'Only p-block elements experience nephelauxetic effects',
      ],
      0,
      'Nephelauxetic ("cloud-expanding"): ligand electrons repel d-electrons, expanding d-orbital size; reduced d–d repulsion → smaller Δ → red shift.',
    ],
  ],
  phd: [
    [
      'In the angular overlap model (AOM), how do σ and π interactions contribute separately to d-orbital energies?',
      [
        'σ-donation and π-repulsion act as independent overlaps',
        'σ and π interactions have identical effects on all d-orbitals',
        'π-interactions have no effect on d-orbital energy levels',
        'All d-orbitals are equally stabilized by both σ and π interactions',
      ],
      0,
      'AOM: σ-bonding raises all d-orbitals; π-repulsion (perpendicular axes) raises dxy, dxz, dyz. Allows quantitative prediction of Δ.',
    ],
    [
      'What is the Bailar twist mechanism, and what does it explain?',
      [
        'An octahedral ↔ trigonal-prismatic pseudorotation',
        'A one-electron redox process yielding odd-electron intermediates',
        'Spontaneous decomposition of a complex at high temperature',
        'A symmetric rotation with no geometric changes',
      ],
      0,
      'Bailar twist: continuous octahedral → trigonal prism → octahedral distortion permutes ligand positions; explains stereochemical inversion in inert d3, d6 complexes.',
    ],
    [
      'How do σ-donation and π-backbonding in the M–CO bond reinforce one another (synergic effect)?',
      [
        'σ-donation raises density, boosts backdonation',
        'π-backbonding cancels σ-donation effects',
        'σ-donation and π-backbonding are independent',
        'Only weak-field CO exhibits both σ and π interactions',
      ],
      0,
      'Synergic cycle: M→CO σ + CO←M π mutually reinforce; higher metal e− density strengthens both, making M–CO bonds exceptionally strong.',
    ],
    [
      'In the Born–Haber cycle, what is the lattice enthalpy and how is it related to crystal structure?',
      [
        'Energy to separate one mole of ionic solid into gaseous ions',
        'Lattice enthalpy is the enthalpy of fusion',
        'Lattice enthalpy is the enthalpy of vaporization of one element',
        'Lattice enthalpy predicts only the color of the compound',
      ],
      0,
      'ΔHₗₐₜₜ ∝ (charges)/(inter-ionic distance); follows Born-Landé equation; larger charges and smaller ions increase ΔHₗₐₜₜ.',
    ],
    [
      'What role does spin–orbit coupling (SOC) play in electronic structure and spectroscopy of heavy transition metals?',
      [
        'SOC mixes spin and orbital momentum',
        'SOC is negligible for all first-row transition metals',
        'SOC makes singlet states unable to relax',
        'Only heavy metals exhibit SOC effects',
      ],
      0,
      'SOC ∝ Z^4; critical for 4d/5d metals; induces weak d–d transitions to become allowed (oscillator strength increase), raising absorbance intensities.',
    ],
    [
      'What is the role of the P-cluster in nitrogenase, and how does its redox state facilitate N2 reduction?',
      [
        'The P-cluster shuttles electrons to the FeMo-cofactor',
        'The P-cluster has a fixed oxidation state and does not participate in electron transfer',
        'The P-cluster stores sulfide for N2 binding',
        'P-clusters are evolutionary relics with no function',
      ],
      0,
      'Nitrogenase P-cluster cycles between oxidation states, serving as the primary electron-transfer bridge; its redox potential is tuned by the protein.',
    ],
    [
      'What is the quantitative difference in packing efficiency between FCC and BCC structures?',
      [
        'FCC packs ~74% (CN 12); BCC packs ~68% (CN 8)',
        'BCC and FCC have identical packing efficiency',
        'BCC is always denser than FCC',
        'Packing fraction is independent of crystal geometry',
      ],
      0,
      'Close-packed structures (FCC, HCP): 74% packing, CN = 12; BCC: 68% packing, CN = 8. Packing efficiency is a fundamental structural descriptor.',
    ],
    [
      'In heterogeneous catalysis, what are ensemble effects and how do they control surface reactivity?',
      [
        'Reactivity depends on surface atom geometry and state',
        'All metal surfaces are chemically equivalent',
        'Heterogeneous catalysis depends only on bulk metal properties',
        'Surface structure has no influence on reaction mechanism',
      ],
      0,
      'Ensemble effects: substrate binds to multiple metals in a specific arrangement; changes in site density or compressive strain alter Ea and TOF.',
    ],
    [
      'What is the D4h point group, and which metal complexes adopt this geometry?',
      [
        'Square-planar geometry: Pt(II), Au(III), Ni(II) complexes',
        'Tetrahedral complexes only; D4h cannot describe planar structures',
        'D4h is exclusive to octahedral geometries',
        'D4h is not a valid point group for transition metal complexes',
      ],
      0,
      'Square-planar D4h: 4 ligands in plane, metal at center. Examples: [PtCl4]2−, [AuCl4]−, cis-platin. Often features d8 metals.',
    ],
    [
      'In bonding models of transition metal carbonyls, explain the difference between synergic bonding and non-bonding electron repulsion.',
      [
        'Synergic bonding reinforces M–CO; repulsion is incidental',
        'Synergic bonding and non-bonding overlap are the same concept',
        'Non-bonding overlap is required for M–CO bonds; synergic bonding is rare',
        'Only CO exhibits synergic bonding; other ligands do not',
      ],
      0,
      'Synergic effect amplifies M–CO bonding via feedback; non-bonding e−–e− repulsion is a separate destabilizing factor. Synergy dominates.',
    ],
    [
      'What is oxidation state ambiguity in transition metal complexes with redox-active ligands?',
      [
        'Redox-active ligands make oxidation state assignment ambiguous',
        'Oxidation states are always unambiguously assigned for all complexes',
        'Redox-active ligands are imaginary and do not exist',
        'Only metals, never ligands, participate in redox chemistry',
      ],
      0,
      'Redox-active ligands (bipy, phen, semiquinone): π-accepting or radical species that cycle oxidation states; true valence-state assignment is ambiguous.',
    ],
    [
      'In perovskite structures (ABX3), what does the Goldschmidt tolerance factor predict?',
      [
        't = (rₐ+rₓ)/[√2(r_B+rₓ)]; t ≈ 1 is cubic',
        'The tolerance factor is irrelevant to perovskite stability',
        't is always 1 for all perovskites',
        'The tolerance factor predicts only color',
      ],
      0,
      'Tolerance factor: t ≈ 1 cubic perovskite; t < 1 tilting, octahedral rotation. Balances cation–anion radius ratios geometrically.',
    ],
    [
      'What is the Tolman electronic parameter (TEP) and how is it used in ligand characterization?',
      [
        'TEP is the CO frequency of [Ni(L)(CO)3]',
        'TEP measures ligand steric bulk only',
        'TEP is defined only for nitrogen-donor ligands',
        'Higher ν_CO values always indicate stronger donors',
      ],
      0,
      'Tolman parameter (cm−1): electron-rich ligands donate to Ni, lowering ν_CO; electron-poor (π-acceptors) withdraw, raising ν_CO. Quantitative donating ranking.',
    ],
    [
      'How do distortions from octahedral symmetry affect the d-orbital diagram?',
      [
        'Lowered symmetry splits d-orbitals further',
        'Distortions have no effect on d-orbital energies',
        'All distortions raise all d-orbitals equally',
        'Distortions increase octahedral symmetry',
      ],
      0,
      'Symmetry reduction (D4h, C3v, etc.) removes d-orbital degeneracy; axis-dependent orbitals split. This electronic origin drives Jahn–Teller distortions.',
    ],
    [
      'In heterometallic cluster complexes, what is the advantage of mixed-metal sites over homometallic clusters?',
      [
        'Different metals activate distinct substrates at nearby sites',
        'All metals in a cluster always behave identically',
        'Mixed-metal clusters are always less stable than single-metal clusters',
        'Different metals compete for the same site and reduce overall activity',
      ],
      0,
      'Heterometallic clusters: different metals tune redox potentials, substrate activation, and enable cooperative mechanisms (e.g., Fe–Co–S nitrogenase mimic).',
    ],
  ],
  professor: [
    [
      'How does ligand-field density-functional theory (LF-DFT) improve upon crystal field theory?',
      [
        'LF-DFT adds quantum exchange and correlation',
        'LF-DFT is simply crystal field theory rebranded',
        'LF-DFT predicts only geometry',
        'LF-DFT has been superseded',
      ],
      0,
      'LF-DFT: all-electron or embedded-cluster DFT with ligand-field analysis; captures exchange/correlation and covalent mixing absent in CFT.',
    ],
    [
      'What is the spectroscopic origin of the yellow color in [Fe(H2O)6]3+, and why does LMCT dominate absorption?',
      [
        'Intense LMCT dwarfs weak d–d transitions',
        '[Fe(H2O)6]3+ is colorless',
        'Water is a strong-field ligand yielding low-spin Fe(III)',
        'All iron aquo complexes have identical spectra',
      ],
      0,
      '[Fe(H2O)6]3+ shows weak d–d (pale yellow); intense LMCT band (~400 nm) dominates because Fe(III) readily oxidizes H2O (Pourbaix diagram).',
    ],
    [
      'How does spin–orbit coupling facilitate intersystem crossing and nonradiative decay in metal complex photodynamics?',
      [
        'SOC lowers the ISC barrier by state mixing',
        'Spin–orbit coupling prevents all excited-state decay',
        'SOC is irrelevant to metal complex photophysics',
        'Only organic dyes experience intersystem crossing',
      ],
      0,
      'SOC (strong in 4d/5d) mixes S1 and T1 wavefunctions; lowers ISC barrier, promotes S1 → T1 transition, enabling long-lived phosphorescence.',
    ],
    [
      'What DFT-based framework explains substrate selectivity in nitrogenase (N2 vs. N2H+ reduction)?',
      [
        'Redox-dependent activation and cooperativity',
        'Nitrogenase selects randomly',
        'Steric exclusion alone explains selectivity',
        'Quantum tunneling is the sole mechanism',
      ],
      0,
      'Nitrogenase selectivity: DFT shows hydride intermediates favor N2H+ at specific redox states; site geometry and cluster e−-transfer pathways enforce discrimination.',
    ],
    [
      'What is the p–d exchange interaction in transition metal oxides, and how does it control electronic properties?',
      [
        'Mixing metal d-bands with ligand p-bands',
        'p–d exchange is negligible in all oxides',
        'Only the d-band contributes; p-bands are inert',
        'p–d exchange always increases conductivity',
      ],
      0,
      'p–d mixing: ligand p-electrons couple to metal d-spins; lowers charge-transfer gap and is critical to Mott insulator vs. conductor boundary.',
    ],
    [
      'How does Marcus–Hush electron-transfer theory address reorganization energy in metal-complex redox catalysis?',
      [
        'λᵢₙₙₑᵣ (vibrational) + λₒᵤₜₑᵣ (solvent)',
        'Marcus theory is outdated',
        'Reorganization energy depends only on temperature',
        'All redox reactions have identical λ',
      ],
      0,
      'Marcus theory: λᵢₙₙₑᵣ (geometry change) + λₒᵤₜₑᵣ (solvent reorientation); high λ shifts reaction-coordinate minimum, raising Ea and overpotential.',
    ],
    [
      'What distinguishes inner-sphere from outer-sphere electron-transfer mechanisms catalyzed by transition metal complexes?',
      [
        'Inner-sphere uses a bridge; outer-sphere does not',
        'Inner-sphere and outer-sphere mechanisms are identical',
        'Only outer-sphere mechanisms occur in biological systems',
        'Inner-sphere mechanisms always require a fully filled d-shell',
      ],
      0,
      'Inner-sphere: high rate ∝ bridge stability (OH− bridging Fe(II)–Fe(III)); outer-sphere: rate ∝ overlap at distance (hexaammine redox partners).',
    ],
    [
      'In homogeneous catalysis, what is the catalytic turnover number (TON) and what factors limit it?',
      [
        'TON ratio, limited by deactivation',
        'TON is the speed of one catalytic cycle',
        'TON is independent of catalyst structure',
        'All homogeneous catalysts achieve infinite TON',
      ],
      0,
      'TON quantifies catalyst robustness; high TON requires kinetic stability against deactivation: ligand dissociation, redox-side reactions, precipitation.',
    ],
    [
      'In Type I blue copper centers, what electronic states enable rapid electron-transfer cycling in enzymes?',
      [
        'Distorted Cu(II) d9 allows fast cycling',
        'Blue copper centers are always Cu(I) d10',
        'Blue copper has no electron-transfer role',
        'Blue color indicates copper poisoning',
      ],
      0,
      'Type I Cu(II): d9, intense LMCT (600 nm); geometrically optimized for rapid ET between redox partners; found in azurin, plantacyanin.',
    ],
    [
      'Why does [Fe(H2O)6]2+ have weak d–d transitions while FeS clusters show intense Fe–S charge-transfer absorption?',
      [
        'FeS clusters show strong Fe–S charge transfer',
        '[Fe(H2O)6]2+ is always blue',
        'All iron complexes exhibit identical CT transitions',
        'Charge-transfer is irrelevant to iron biochemistry',
      ],
      0,
      '[Fe(H2O)6]2+ (d6, pale green) shows weak d–d (Δ ~10,000 cm−1); FeS clusters (nitrogenase, ferredoxin) display intense Fe–S MLCT due to high covalency.',
    ],
    [
      'How does the electronic nature of excited states (MC, MLCT, LMCT) influence photocatalytic performance in dye-sensitized solar cells?',
      [
        'Long-lived MLCT aids electron injection into TiO2',
        'All excited states are equally useful for photocatalysis',
        'Excited-state nature is irrelevant to photocatalytic efficiency',
        'Only LMCT states are useful',
      ],
      0,
      'DSSC: MLCT excited state of Ru(bpy)3 is long-lived (τ ~1 μs) and tuned for electron injection into TiO2 conduction band (E_inj favorable).',
    ],
    [
      'What does the Creutz–Taube complex [(NH3)5Ru–pyrazine–Ru(NH3)5]5+ reveal about valence delocalization?',
      [
        'Shows Class III delocalization via pyrazine',
        'Valence delocalization never occurs',
        'The Creutz–Taube complex is always trapped valence',
        'Bridging ligands prevent electron transfer',
      ],
      0,
      'Creutz–Taube: Class III mixed-valence (delocalized); IR/magnetic data show averaged Ru(II.5) valence; pyrazine π allows rapid inter-metal ET.',
    ],
    [
      'How do continuum solvation models (PCM, COSMO) refine DFT predictions of metal-complex redox potentials?',
      [
        'Models bulk-solvent screening of Coulomb effects',
        'Solvation models have no effect on redox potential predictions',
        'Redox potentials depend exclusively on oxidation state',
        'Only explicit water molecules are relevant',
      ],
      0,
      'Continuum solvation (PCM): screens long-range Coulomb effects on oxidized/reduced states; refines computed E° predictions to within 0.1–0.2 V.',
    ],
    [
      'What is the role of hemilabile ligands in catalysis, and how do they differ from static chelating ligands?',
      [
        'One donor arm dissociates reversibly, opening a substrate site',
        'Hemilabile ligands are permanently attached',
        'Static and hemilabile ligands have identical catalytic properties',
        'Hemilabile ligands only appear in stoichiometric reactions',
      ],
      0,
      'Hemilabile design: tailored dissociation energy of one arm balances substrate access (open site) with stability (one arm bound); improves TON/TOF.',
    ],
    [
      'What is the computational challenge in predicting spin-crossover (SCO) temperatures for iron(II) complexes?',
      [
        'Accurate ΔS_vib and electronic entropy are needed since ΔG is small',
        'SCO temperatures are experimentally fixed and never vary',
        'Only the enthalpy of the transition matters; entropy is irrelevant',
        'Computational methods always overestimate SCO temperatures by orders of magnitude',
      ],
      0,
      'SCO: DFT-predicted T_SCO requires accurate ΔH and ΔS (vibrational, electronic); narrow ΔG margin (~1 kJ/mol) demands high accuracy.',
    ],
  ],
};
